import classNames from "components/ui/classNames";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useEffect, useRef } from "react";
import type { ChevronProps, DayButtonProps, DayPickerProps, RootProps, WeekNumberProps } from "react-day-picker";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import { enUS, ptBR } from "react-day-picker/locale";
import { useIntl } from "react-intl";

function Calendar({
  className,
  showOutsideDays = true,
  captionLayout = "label",
  formatters,
  components,
  ...props
}: DayPickerProps) {
  const intl = useIntl();
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={classNames(
        "p-4 [--cell-size:--spacing(8)]",
        "w-full",
        "[[data-slot=card-content]_&]:bg-transparent",
        "[[data-slot=popover-content]_&]:bg-transparent",
        "group/calendar",
        "border border-gray-300 bg-white dark:border-slate-700 dark:bg-slate-800",
        "rounded-3xl",
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className,
      )}
      locale={intl.locale === "pt-BR" ? ptBR : enUS}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: (date) => {
          return date.toLocaleString("default", {
            month: "short",
          });
        },
        ...formatters,
      }}
      classNames={{
        root: classNames("w-full", defaultClassNames.root),
        months: classNames("relative flex flex-col gap-4 md:flex-row", defaultClassNames.months),
        month: classNames("flex w-full flex-col gap-4", defaultClassNames.month),
        nav: classNames(
          "absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1",
          defaultClassNames.nav,
        ),
        button_previous: classNames(
          "size-(--cell-size) select-none p-0 aria-disabled:opacity-50",
          defaultClassNames.button_previous,
        ),
        button_next: classNames(
          "size-(--cell-size) select-none p-0 aria-disabled:opacity-50",
          defaultClassNames.button_next,
        ),
        month_caption: classNames(
          "h-(--cell-size) px-(--cell-size) flex w-full items-center justify-center",
          defaultClassNames.month_caption,
        ),
        dropdowns: classNames(
          "h-(--cell-size) flex w-full items-center justify-center gap-1.5 text-sm font-medium",
          defaultClassNames.dropdowns,
        ),
        dropdown_root: classNames(
          "has-focus:border-indigo-300 shadow-xs has-focus:ring-indigo-100 has-focus:ring-[3px] relative rounded-md border border-gray-300",
          "dark:has-focus:border-indigo-600 dark:has-focus:ring-indigo-900/50 dark:border-slate-700",
          defaultClassNames.dropdown_root,
        ),
        dropdown: classNames("absolute inset-0 bg-white opacity-0 dark:bg-slate-800", defaultClassNames.dropdown),
        caption_label: classNames(
          "select-none font-medium",
          "text-slate-800 dark:text-slate-300",
          captionLayout === "label"
            ? "text-sm font-bold"
            : "flex h-8 items-center gap-1 rounded-md pl-2 pr-1 text-sm [&>svg]:size-3.5 [&>svg]:text-gray-500 dark:[&>svg]:text-slate-400",
          defaultClassNames.caption_label,
        ),
        table: "w-full border-collapse",
        weekdays: classNames("flex", defaultClassNames.weekdays),
        weekday: classNames(
          "flex-1 select-none rounded-md text-[0.8rem] text-gray-500 dark:text-slate-400",
          "font-bold text-slate-600 dark:text-slate-100",
          defaultClassNames.weekday,
        ),
        week: classNames("mt-2 flex w-full", defaultClassNames.week),
        week_number_header: classNames("w-(--cell-size) select-none", defaultClassNames.week_number_header),
        week_number: classNames(
          "select-none text-[0.8rem] text-gray-500 dark:text-slate-400",
          defaultClassNames.week_number,
        ),
        day: classNames(
          "group/day relative aspect-square h-full w-full select-none p-0 text-center [&:last-child[data-selected=true]_button]:rounded-r-md",
          props.showWeekNumber
            ? "[&:nth-child(2)[data-selected=true]_button]:rounded-l-md"
            : "[&:first-child[data-selected=true]_button]:rounded-l-md",
          defaultClassNames.day,
        ),
        range_start: classNames("rounded-l-md bg-teal-50 dark:bg-teal-900/20", defaultClassNames.range_start),
        // range_middle: classNames("rounded-none", defaultClassNames.range_middle),
        range_middle: defaultClassNames.range_middle,
        range_end: classNames("rounded-r-md bg-teal-50 dark:bg-teal-900/20", defaultClassNames.range_end),
        today: classNames(
          "rounded-md font-bold",
          "data-[selected=false]:bg-blue-50 data-[selected=false]:text-blue-600",
          "dark:data-[selected=false]:bg-orange-600/20 dark:data-[selected=false]:text-orange-300",
          defaultClassNames.today,
        ),
        outside: classNames(
          "text-gray-400 aria-selected:text-gray-400 dark:text-slate-500 dark:aria-selected:text-slate-500",
          defaultClassNames.outside,
        ),
        disabled: classNames("text-gray-400 opacity-50 dark:text-slate-500", defaultClassNames.disabled),
        hidden: classNames("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: CalendarRoot,
        Chevron: CalendarChevron,
        DayButton: CalendarDayButton,
        WeekNumber: CalendarWeekNumber,
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarRoot({ className, rootRef, ...props }: RootProps) {
  return <div data-slot="calendar" ref={rootRef} className={classNames(className)} {...props} />;
}

function CalendarChevron({ className, orientation, ...props }: ChevronProps) {
  if (orientation === "left") {
    return <ChevronLeftIcon className={classNames("size-4 text-gray-500 dark:text-slate-300", className)} {...props} />;
  }

  if (orientation === "right") {
    return (
      <ChevronRightIcon className={classNames("size-4 text-gray-500 dark:text-slate-300", className)} {...props} />
    );
  }

  return <ChevronDownIcon className={classNames("size-4 text-gray-500 dark:text-slate-300", className)} {...props} />;
}
function CalendarWeekNumber({ children, className, ...props }: WeekNumberProps) {
  return (
    <td {...props}>
      <div
        className={classNames(
          "size-(--cell-size) flex items-center justify-center text-center text-gray-500 dark:text-slate-300",
          className,
        )}
      >
        {children}
      </div>
    </td>
  );
}

function CalendarDayButton({ className, day, modifiers, ...props }: DayButtonProps) {
  const defaultClassNames = getDefaultClassNames();
  const ref = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (modifiers.focused) {
      ref.current?.focus();
    }
  }, [modifiers.focused]);

  const isSelectedSingle =
    modifiers.selected &&
    !modifiers.range_start &&
    !modifiers.range_end &&
    !modifiers.range_middle &&
    !modifiers.outside;

  const isOutsideSelectedRange =
    !modifiers.selected &&
    !modifiers.range_start &&
    !modifiers.range_end &&
    !modifiers.range_middle &&
    !modifiers.today;

  return (
    <button
      ref={ref}
      type="button"
      data-day={day.date.toLocaleDateString()}
      data-selected-single={isSelectedSingle}
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      data-outside-selected-range={isOutsideSelectedRange}
      data-outside={modifiers.outside && !modifiers.selected}
      className={classNames(
        "flex size-full items-center justify-center",
        "data-[selected-single=true]:bg-teal-600 data-[selected-single=true]:text-white",

        // Range styles
        "data-[range-middle=true]:rounded-none data-[range-middle=true]:bg-teal-50 data-[range-middle=true]:text-teal-700",
        "data-[range-middle=true]:font-medium data-[range-middle=true]:dark:bg-teal-900/20 data-[range-middle=true]:dark:text-teal-400",

        // Range start and end styles
        "data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md data-[range-start=true]:bg-teal-600 data-[range-start=true]:text-white",
        "data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-end=true]:bg-teal-600 data-[range-end=true]:text-white",
        "data-[range-end=true]:font-bold data-[range-start=true]:font-bold",

        // Focused styles
        "group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:border-indigo-300",
        "group-data-[focused=true]/day:dark:border-indigo-600 group-data-[focused=true]/day:dark:ring-indigo-900/50",

        // Outside selected range styles
        "data-[outside-selected-range=true]:text-slate-500 dark:data-[outside-selected-range=true]:text-slate-400",

        // Outside styles
        "data-[outside=true]:text-slate-500 dark:data-[outside=true]:text-slate-600",

        // Common styles
        "flex aspect-square w-full flex-col gap-1 leading-none",
        "[&>span]:text-xs [&>span]:opacity-70",
        "min-w-(--cell-size)",
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
